import React, { useMemo, useState } from 'react'
import StoreUtils from '../../utils/storeUtils'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { selectActiveCallCard } from '../../reducers/uiSlice'
import { selectCurrentStore } from '../../reducers/storeSlice'
import { useStore, useViews } from 'state'
import { PDPContainer, Modal, useProducts, useCart, usePDP, Products as ProductsList } from 'ui'
import { AnimatePresence } from 'framer-motion'
import { Checkout } from '../GJKit/Checkout'
import { OrderStatus } from '../Checkout/OrderStatus'
import { useAuth } from '@gojiraf/auth'
import { JoinChatForm } from '../Chat/JoinChatForm'
import { Products } from '../Products/Products'
import { useCheckout } from '../../hooks/useCheckout'
import { ChatModule } from '../Chat/ChatModule'
import { usePostHogAnalytics } from '@gojiraf/analytics'
import { ActionBar } from './ActionBar'
import { ActionBarWrapper } from './Layout.styles'

const CallAction = () => {
  const [chatOpen, setChatOpen] = useState(true)
  const { event } = useStore()
  const { postHogTrackEvent } = usePostHogAnalytics()
  const { user } = useAuth()
  const store = useSelector(selectCurrentStore)
  const {
    storeConfigurations: {
      features: { chat },
    },
  } = store
  const activeCallCard = useSelector(selectActiveCallCard)
  const { type: paymentType } = store.paymentGateways.find(
    (paymentGatewayItem) => paymentGatewayItem.isDefault === true,
  )
  const isLightIntegration = StoreUtils.getIsLightIntegration(store.paymentGateways)
  const {
    handlePIPLightIntegration,
    getItemsInCart,
    hasOwnCheckout,
    isVtexExternalCart,
    handleAddProductToCart,
    handleOnBuyClicked,
    defaultDeliveryMethod,
  } = useCheckout(store)
  const { send: sendPDP } = usePDP()
  const { state, send: sendViews } = useViews()
  const { state: cartState } = useCart()
  const canBuy = useMemo(
    () => !!cartState.context.variants?.size,
    [cartState.context.variants?.size],
  )
  const { products, getProductsBySearchTerm } = useProducts({
    callId: event?.eventId,
  })

  const showProducts = () => {
    sendViews({
      type: 'SHOW_PRODUCTS',
    })
  }

  const onProductsListClose = () => {
    sendViews({
      type: 'SHOW_CHAT',
    })
  }

  const handleItemClicked = (product) => {
    postHogTrackEvent('product-click-detail-button', {
      productName: product.name,
      from: 'product',
    })
    sendPDP({
      type: 'SET_PRODUCT',
      productId: product.id,
    })
    sendViews({
      type: 'SHOW_ITEM',
    })
  }

  const goBack = () => {
    sendViews({
      type: 'GO_BACK',
    })
  }

  return (
    <>
      {chat.enabled ? (
        <>
          <ChatModule chatOpen={chatOpen} setChatOpen={setChatOpen} user={user} />
          {state.matches('secondary.showingJoinChat') === true && (
            <Modal size="SMALL" onClose={goBack}>
              <JoinChatForm />
            </Modal>
          )}
        </>
      ) : (
        <ActionBarWrapper>
          <ActionBar />
        </ActionBarWrapper>
      )}

      <AnimatePresence>
        {state.matches('secondary.showingProducts') === true && (
          <Modal size="SMALL" onClose={onProductsListClose}>
            <ProductsList
              defaultDeliveryMethod={defaultDeliveryMethod}
              onBuyClicked={handleOnBuyClicked}
              getProductsBySearchTerm={getProductsBySearchTerm}
              paymentType={paymentType}
              isLightIntegration={isLightIntegration}
              isVtexExternalCart={isVtexExternalCart}
              canBuy={canBuy}
              products={products}
              onItemClicked={handleItemClicked}
            />
          </Modal>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {state.matches('main.showingCart') === true && (
          <Modal size="LARGE" onClose={goBack}>
            <Products products={products} />
          </Modal>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {state.matches('main.showingPDP') === true && (
          <Modal size="LARGE" onClose={goBack}>
            <PDPContainer
              getItemsInCart={getItemsInCart}
              handlePIPLightIntegration={handlePIPLightIntegration}
              isLightIntegration={isLightIntegration}
              isVtexExternalCart={isVtexExternalCart}
              hasOwnCheckoutIntegration={hasOwnCheckout}
              handleAddProductToCart={handleAddProductToCart}
              handleBuyClicked={handleOnBuyClicked}
              goBack={showProducts}
            />
          </Modal>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {state.matches('main.showingOrder') === true && (
          <Modal onClose={showProducts}>
            <Checkout onGoBack={goBack} />
          </Modal>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {state.matches('main.showingOrderStatus') === true && (
          <Modal onClose={goBack}>
            <OrderStatus activeCallCard={activeCallCard} />
          </Modal>
        )}
      </AnimatePresence>
      {state.matches('secondary.showingNone') === true && null}
    </>
  )
}

CallAction.propTypes = {
  setInitChat: PropTypes.func.isRequired,
}

export { CallAction }
